import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { KeyRound, Copy, Trash2, Plus, ShieldCheck } from "lucide-react";

export default function GatePhoneTokens() {
  const [tokens, setTokens] = useState([]);
  const [fresh, setFresh] = useState(null); // one-time revealed token value
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/tokens");
      setTokens(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tokens/readonly", { name: "GATE_PHONE_TEST", days: 30 });
      setFresh(data.token);
      toast.success("Read-only token vytvořen. Zkopíruj ho teď — znovu se nezobrazí.");
      load();
    } catch (e) {
      toast.error("Token se nepodařilo vytvořit.");
    } finally { setBusy(false); }
  };

  const revoke = async (jti) => {
    try {
      await api.post(`/admin/tokens/${jti}/revoke`);
      toast.success("Token revokován.");
      load();
    } catch { toast.error("Revokace selhala."); }
  };

  const copyFresh = async () => {
    try { await navigator.clipboard.writeText(fresh); toast.success("Zkopírováno do schránky."); }
    catch { toast.error("Kopírování selhalo — označ a zkopíruj ručně."); }
  };

  return (
    <div className="border border-white/10 bg-[#0b0a09] p-4 space-y-4" data-testid="gate-phone-tokens">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-nv" />
        <p className="font-head text-sm text-nv tracking-widest uppercase">GATE Phone — read-only API tokeny</p>
      </div>
      <p className="text-[11px] text-neutral-500">
        Vygeneruje <span className="text-neutral-300">read-only</span> token (jen čtení, platnost 30 dní, revokovatelný).
        Zadej ho ručně do telefonu: <span className="text-neutral-300">Nastavení → GATE API → Token</span>. Nikam ho neukládej do kódu.
      </p>

      <Button data-testid="gen-readonly-token-btn" onClick={generate} disabled={busy || !!fresh}
        className="vhs-btn rounded-none bg-red-700 hover:bg-red-600">
        <Plus className="w-4 h-4 mr-2" />{busy ? "Generuji…" : "Vygenerovat GATE_PHONE_TEST token"}
      </Button>

      {fresh && (
        <div className="border border-nv/40 bg-black p-3 space-y-2" data-testid="fresh-token-box">
          <p className="text-[11px] text-yellow-500">⚠ Zkopíruj teď — po zavření se hodnota už nikdy nezobrazí.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[11px] text-neutral-200 break-all bg-[#111] p-2 border border-white/10" data-testid="fresh-token-value">{fresh}</code>
            <Button data-testid="copy-token-btn" aria-label="Kopírovat token" onClick={copyFresh} variant="outline" className="rounded-none border-white/10 bg-[#111]"><Copy className="w-4 h-4" /></Button>
          </div>
          <Button data-testid="hide-token-btn" onClick={() => setFresh(null)} variant="outline" className="rounded-none border-white/10 bg-[#111] text-neutral-400 text-xs">Zavřít (skrýt hodnotu)</Button>
        </div>
      )}

      <div className="space-y-2">
        {tokens.length === 0 && <p className="text-[11px] text-neutral-600">Zatím žádné tokeny.</p>}
        {tokens.map((t) => (
          <div key={t.jti} className="flex items-center justify-between border border-white/10 bg-black px-3 py-2 text-xs" data-testid={`token-row-${t.jti}`}>
            <div className="flex items-center gap-2 text-neutral-300">
              <KeyRound className="w-3 h-3 text-neutral-500" />
              <span>{t.name || "device"}</span>
              <span className="text-neutral-600">•••••••• (skryto)</span>
              {t.revoked ? <span className="text-red-500">REVOKOVÁN</span> : <span className="text-green-600">aktivní</span>}
              <span className="text-neutral-600">exp: {(t.expires_at || "").slice(0, 10)}</span>
            </div>
            {!t.revoked && (
              <button data-testid={`revoke-token-${t.jti}`} onClick={() => revoke(t.jti)} className="text-neutral-500 hover:text-red-500 flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Revokovat
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
